/**
 * What the step being followed still asks somebody to hold.
 *
 * One step can ask for several things and each of them has a place it has to
 * end up: in the bag, in a gear slot, or on the belt. The same pickaxe wielded
 * and the same pickaxe belted are two different answers, so where it is matters
 * as much as whether it is held at all.
 *
 * Worked out in one place because two parts of the interface ask the same
 * question: the tracker, which says what is still missing, and the bag, which
 * lights up the slot holding the thing that is wanted.
 */

import type { ItemStack } from '@inverse/common';
import { QuestWantPlace, type QuestStepWire, type QuestWantWire } from '@inverse/protocol';

/** Everywhere something can be held, as the panels have it. */
export interface Holding {
  inventory: readonly (ItemStack | null)[];
  worn: readonly (ItemStack | null)[];
  toolbelt: readonly (ItemStack | null)[];
}

export interface Wanted extends QuestWantWire {
  /** How many are where the step wants them. */
  have: number;
  /** Whether that is enough. */
  met: boolean;
}

function countIn(slots: readonly (ItemStack | null)[], itemId: number): number {
  let held = 0;
  for (const slot of slots) {
    if (slot && slot.id === itemId) held += slot.quantity;
  }
  return held;
}

/** Every thing the step asks for, said with how far along each one is. */
export function wantsOf(step: QuestStepWire | undefined, holding: Holding): Wanted[] {
  if (!step) return [];

  return step.wants
    .filter((want) => want.itemId > 0)
    .map((want) => {
      const where =
        want.where === QuestWantPlace.Worn
          ? holding.worn
          : want.where === QuestWantPlace.Belt
            ? holding.toolbelt
            : holding.inventory;

      const have = countIn(where, want.itemId);
      return { ...want, have, met: have >= Math.max(1, want.count) };
    });
}

/**
 * The items a bag slot should be lit up for.
 *
 * Only the ones still wanted: a slot that lights up after the thing has been
 * put on is a slot saying something that is no longer true. Something wanted
 * worn is lit while it sits in the bag, which is exactly the moment somebody
 * needs telling that this is the pickaxe the quest meant.
 */
export function wantedItems(wants: readonly Wanted[]): ReadonlySet<number> {
  const held = new Set<number>();
  for (const want of wants) {
    if (!want.met) held.add(want.itemId);
  }
  return held;
}

/** Where something has to end up, in the words the tracker uses. */
export function placeSaid(where: QuestWantPlace): string {
  if (where === QuestWantPlace.Worn) return 'worn';
  if (where === QuestWantPlace.Belt) return 'on the belt';
  return '';
}
